import {
  createTanstackCollections,
  CreateTanstackCollectionsOptions,
  TanstackDatabaseCollections,
  SmartContextsCollection,
  SmartViewsCollection,
  SmartCardsCollection,
} from "./collections";
import {
  TanstackRepositorySet,
  createRepositories,
} from "@/domains/tanstack-db/repositories/registry";
import {
  assertSupportedTanstackStorage,
  getDefaultTanstackStorage,
  tanstackStorageConfigsEqual,
  TanstackDatabaseStorageConfig,
} from "./storage-kind";
import { controlFlowModule } from "@/modules/control-flow";

export interface TanstackDatabaseInitOptions {
  spaceAccountId: string;
}

export enum TanstackDatabaseState {
  UNINITIALIZED = "uninitialized",
  INITIALIZED = "initialized",
}

export interface TanstackDatabaseManagerOptions
  extends Omit<CreateTanstackCollectionsOptions, "storage"> {
  storage?: TanstackDatabaseStorageConfig;
}

export class TanstackDatabaseManager {
  private static instance: TanstackDatabaseManager | null = null;

  private collections: TanstackDatabaseCollections;
  private repositories: TanstackRepositorySet;
  private initialized = false;
  private currentSpaceAccountId: string | null = null;
  private readonly storage: TanstackDatabaseStorageConfig;

  constructor(options: TanstackDatabaseManagerOptions = {}) {
    const storage = options.storage ?? getDefaultTanstackStorage();
    assertSupportedTanstackStorage(storage);

    this.storage = storage;
    this.collections = createTanstackCollections({ storage });
    this.repositories = createRepositories(this.collections);
  }

  static getInstance(options: TanstackDatabaseManagerOptions = {}): TanstackDatabaseManager {
    if (!this.instance) {
      this.instance = new TanstackDatabaseManager(options);
      return this.instance;
    }

    if (options.storage && !tanstackStorageConfigsEqual(this.instance.storage, options.storage)) {
      controlFlowModule.notImplementedError({
        message:
          "TanstackDatabaseManager cannot be reconfigured with a different storage kind at runtime.",
      });
    }

    return this.instance;
  }

  async init(options: TanstackDatabaseInitOptions): Promise<void> {
    const { spaceAccountId } = options;

    if (this.initialized && this.currentSpaceAccountId === spaceAccountId) {
      return;
    }

    if (this.currentSpaceAccountId && this.currentSpaceAccountId !== spaceAccountId) {
      this.resetCollections();
    }

    await this.preloadCollections();

    this.currentSpaceAccountId = spaceAccountId;
    this.initialized = true;
  }

  cleanup(): void {
    this.resetCollections();
    this.initialized = false;
    this.currentSpaceAccountId = null;
  }

  async startSync(): Promise<void> {
    // Local-only collections do not require sync, but we preserve the interface.
  }

  async stopSync(): Promise<void> {
    // No-op for local-only data.
  }

  get smartContexts(): TanstackRepositorySet["smartContexts"] {
    return this.repositories.smartContexts;
  }

  get smartViews(): TanstackRepositorySet["smartViews"] {
    return this.repositories.smartViews;
  }

  get smartCards(): TanstackRepositorySet["smartCards"] {
    return this.repositories.smartCards;
  }

  get stats(): TanstackRepositorySet["stats"] {
    return this.repositories.stats;
  }

  get smartContextsCollection(): SmartContextsCollection {
    return this.collections.smartContexts;
  }

  get smartViewsCollection(): SmartViewsCollection {
    return this.collections.smartViews;
  }

  get smartCardsCollection(): SmartCardsCollection {
    return this.collections.smartCards;
  }

  getInitializationState(): TanstackDatabaseState {
    return this.initialized
      ? TanstackDatabaseState.INITIALIZED
      : TanstackDatabaseState.UNINITIALIZED;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  private resetCollections(): void {
    this.collections = createTanstackCollections({ storage: this.storage });
    this.repositories = createRepositories(this.collections);
  }

  private async preloadCollections(): Promise<void> {
    await Promise.all([
      this.collections.smartContexts.preload(),
      this.collections.smartViews.preload(),
      this.collections.smartCards.preload(),
    ]);
  }
}

export type DatabaseState = TanstackDatabaseState;
export { TanstackDatabaseManager as DatabaseManager };
